import { message } from "@/utils/resetMessage";

/**
 * 统一的文件下载入口。
 *
 * ── 为什么要有这个文件 ────────────────────────────────────────────────
 * 原实现散落在 9 个组件里，各自写了一份「建隐藏 iframe，把下载地址塞进 iframe.src」。
 * 下载地址返回真正的附件时没问题，但只要它返回的是 HTML，浏览器就不会下载，
 * 而是把这份 HTML 当成网页加载进 iframe。
 *
 * ── 关于「下载地址返回 HTML」的现状（2026-09-01 核实）────────────────
 * 真实下载地址来自数据库 sys_config：
 *   downloadURL1 = https://down.songzituku.com/topze-dispatch   （阿里云 CDN）
 *   downloadURL2/3 = https://pp.sucaidaixia.com/topze-dispatch  （Cloudflare）
 * 这两个域名实测都正常：application/octet-stream + Content-Disposition: attachment，
 * 且本身就带 CDN 加速。所以【当前线上不存在返回 HTML 的问题】。
 *
 * 曾经怀疑过 vip.qqtuku.com/topze-dispatch/* 会被 nginx try_files 兜底成 index.html
 * （该站点的 nginx 确实没有 /topze-dispatch/ 的 location），但应用根本不请求那个地址，
 * 属于误判，已排除。这里保留说明，避免后人重复踩同一个坑。
 *
 * ── 现有兜底：同源预检 ────────────────────────────────────────────
 * 下载地址与当前站点同源时，先用 Range: bytes=0-0 取一个字节看响应头，
 * 发现是 text/html 就判定链路异常，直接给用户明确提示，不再发起下载——
 * 避免把一份 HTML 塞进 iframe 后在里面把整个 SPA 重新启动（同源、共享 localStorage）。
 * 只对同源做：跨域时 Range 属非安全头会触发 CORS 预检，探测结果不可信，不如不探。
 * 当前生产的下载域名都是跨域，因此这层实际不会触发，等于保持原有行为不变。
 * 探测本身出任何问题都不阻断下载，保证兜底逻辑自己不会把正常下载搞挂。
 *
 * 注：不要给这个 iframe 加 sandbox。sandbox 能挡住「HTML 被当页面执行」，
 * 但会让 iframe 变成 opaque origin，对跨域附件下载的影响无法在本地可靠验证，
 * 而下载是核心付费链路，不接受未经验证的风险。
 */

// 真正触发下载：与原实现完全一致（隐藏 iframe），不加 sandbox，理由见文件头注释
function triggerDownload(url) {
  const iframe = document.createElement("iframe");
  iframe.style.display = "none"; // 防止影响页面
  iframe.style.height = 0; // 防止影响页面
  iframe.src = url;
  document.body.appendChild(iframe); // 这一行必须，iframe挂在到dom树上才会发请求
  // 5分钟之后删除（onload方法对于下载链接不起作用，就先抠脚一下吧）
  setTimeout(() => {
    iframe.remove();
  }, 1000);
}

// 下载地址是否与当前页面同源（同源才做预检，理由见文件头注释）
function isSameOrigin(url) {
  try {
    return new URL(url, window.location.href).origin === window.location.origin;
  } catch (e) {
    return false;
  }
}

function looksLikeHtml(contentType) {
  return (
    String(contentType || "")
      .toLowerCase()
      .indexOf("text/html") > -1
  );
}

export const downloadFile = (url) => {
  if (!url) {
    return;
  }

  // 跨域地址：拿不到可信的响应头，直接走（sandbox 那层仍然生效）
  if (typeof fetch !== "function" || !isSameOrigin(url)) {
    triggerDownload(url);
    return;
  }

  let probe;
  try {
    // 只取 1 个字节，成本可以忽略
    probe = fetch(url, {
      method: "GET",
      headers: { Range: "bytes=0-0" },
      cache: "no-store",
    });
  } catch (e) {
    triggerDownload(url);
    return;
  }

  probe
    .then((res) => {
      if (looksLikeHtml(res.headers.get("Content-Type"))) {
        message.error({
          message: "下载链路异常：服务器返回的是网页而不是文件，请联系客服处理",
          showClose: true,
          duration: 6000,
          offset: 100,
        });
        return;
      }
      triggerDownload(url);
    })
    .catch(() => {
      // 预检失败（网络抖动等）不阻断正常下载
      triggerDownload(url);
    });
};

export default downloadFile;
